import React, { Suspense, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { InstituteProvider } from './contexts/InstituteContext';
import { AccessProvider, useAccess } from './contexts/AccessContext';
import { PermissionProvider, usePermissions } from './contexts/PermissionContext';
import { useTranslation } from 'react-i18next';
import { Toaster } from './components/ui/sonner';
import useGlobalSecurity from './hooks/useGlobalSecurity';
import Enroll from "./pages/Enroll";
import ContinueCourse from './pages/ContinueCourse';
import SubscriptionBanner from './components/layout/SubscriptionBanner';
import AccessBlocked from './pages/AccessBlocked';
import PermissionAlert from './components/ui/PermissionAlert';

// Multi-tenant pages
const SuperAdminInstitutes = React.lazy(() => import('./pages/SuperAdminInstitutes'));
const SuperAdminInstituteCreate = React.lazy(() => import('./pages/SuperAdminInstituteCreate'));
const SuperAdminInstituteDetails = React.lazy(() => import('./pages/SuperAdminInstituteDetails'));
const SuperAdminInstituteDelete = React.lazy(() => import('./pages/SuperAdminInstituteDelete'));
const SubscriptionPlans = React.lazy(() => import('./pages/SubscriptionPlans'));
const SubscriptionPlanManager = React.lazy(() => import('./pages/SubscriptionPlanManager'));
const ManagePermissions = React.lazy(() => import('./pages/ManagePermissions'));
const EmailTemplates = React.lazy(() => import('./pages/superadmin/EmailTemplates'));
const SubscriptionRenewal = React.lazy(() => import('./pages/SubscriptionRenewal'));
const InstituteBranding = React.lazy(() => import('./pages/InstituteBranding'));
const InstituteOnboarding = React.lazy(() => import('./pages/InstituteOnboarding'));
const InstituteRegister = React.lazy(() => import('./pages/InstituteRegister'));
const InstituteAnalytics = React.lazy(() => import('./pages/InstituteAnalytics'));




import logo from './assets/navipro_logo.png';
import './App.css';

const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const InstituteLandingPage = React.lazy(() => import('./pages/InstituteLandingPage'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const VerifyAndPay = React.lazy(() => import('./pages/VerifyAndPay'));
const SetPassword = React.lazy(() => import('./pages/SetPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const GoogleCallback = React.lazy(() => import('./pages/GoogleCallback'));
const LinkedInCallback = React.lazy(() => import('./pages/LinkedInCallback'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const StudentDashboard = React.lazy(() => import('./pages/student/StudentDashboard'));
const Courses = React.lazy(() => import('./pages/Courses'));
const CourseDetail = React.lazy(() => import('./pages/CourseDetail'));
const CourseBuilder = React.lazy(() => import('./pages/CourseBuilder'));
const CourseLandingPage = React.lazy(() => import('./pages/CourseLandingPage'));
const CourseCatalog = React.lazy(() => import('./pages/CourseCatalog'));
const Leaderboard = React.lazy(() => import('./pages/Leaderboard'));
const Tests = React.lazy(() => import('./pages/Tests'));
const Assignments = React.lazy(() => import('./pages/Assignments'));
const ProgrammingEngine = React.lazy(() => import('./components/assignments/ProgrammingEngine'));
const LevelRoadmap = React.lazy(() => import('./components/assignments/LevelRoadmap'));
const TestInstructions = React.lazy(() => import('./components/assignments/TestInstructions'));
const TestEngine = React.lazy(() => import('./components/assignments/TestEngine'));

// Test Components
const TestRoadmap = React.lazy(() => import('./components/tests/TestRoadmap'));
const TestInstructionPage = React.lazy(() => import('./components/tests/TestInstructions'));
const TestEnginePage = React.lazy(() => import('./components/tests/TestEngine'));
const TestResultPage = React.lazy(() => import('./components/tests/TestResult'));
const CompanyQuestions = React.lazy(() => import('./pages/CompanyQuestions'));
const Jobs = React.lazy(() => import('./pages/Jobs'));
const Bookmarks = React.lazy(() => import('./pages/Bookmarks'));
const Wallet = React.lazy(() => import('./pages/Wallet'));
const Profile = React.lazy(() => import('./pages/Profile'));
const DynamicProfile = React.lazy(() => import('./pages/DynamicProfile'));
const ResumeFullPage = React.lazy(() => import('./pages/resume-builder/ResumeFullPage'));
const PublicResume = React.lazy(() => import('./pages/PublicResume'));
const Settings = React.lazy(() => import('./pages/Settings'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const Analytics = React.lazy(() => import('./pages/Analytics'));
const LiveClasses = React.lazy(() => import('./pages/LiveClasses'));
const EnhancedLiveClass = React.lazy(() => import('./pages/EnhancedLiveClass'));
const CodeLab = React.lazy(() => import('./pages/CodeLab'));
const EnhancedCodeLab = React.lazy(() => import('./pages/EnhancedCodeLab'));
const Forum = React.lazy(() => import('./pages/Forum'));
const Chat = React.lazy(() => import('./pages/Chat'));
const Certificates = React.lazy(() => import('./pages/Certificates'));
const LearningPaths = React.lazy(() => import('./pages/LearningPaths'));
const Recordings = React.lazy(() => import('./pages/Recordings'));
const RecordingDetail = React.lazy(() => import('./pages/RecordingDetail'));
const AdminPayments = React.lazy(() => import('./pages/admin/AdminPayments'));
const AdminRecordingManagement = React.lazy(() => import('./pages/admin/AdminRecordingManagement'));
const AdminStudentPaymentSupport = React.lazy(() => import('./pages/admin/AdminStudentPaymentSupport'));
const InstituteSubscriptionPayments = React.lazy(() => import('./pages/admin/InstituteSubscriptionPayments'));
const InstituteSubscriptionPlans = React.lazy(() => import('./pages/admin/InstituteSubscriptionPlans'));
const StudentPayments = React.lazy(() => import('./pages/student/StudentPayments'));
const ExamUsers = React.lazy(() => import('./pages/ExamUsers'));
const PaidExamMarks = React.lazy(() => import('./pages/admin/PaidExamMarks'));
const PaidExamInstructions = React.lazy(() => import('./pages/PaidExamInstructions'));
const SupportUserManagement = React.lazy(() => import('./pages/admin/SupportUserManagement'));
const ReferralSettingsPage = React.lazy(() => import('./pages/admin/ReferralSettings'));

// New Enhanced Pages
const AdminPanel = React.lazy(() => import('./pages/AdminPanel'));
const JobsPortal = React.lazy(() => import('./pages/JobsPortal'));
const CommunityForum = React.lazy(() => import('./pages/CommunityForum'));
const NotificationsCenter = React.lazy(() => import('./pages/NotificationsCenter'));
// const LiveClassesPortal = React.lazy(() => import('./pages/LiveClassesPortal'));
const AssessmentsPortal = React.lazy(() => import('./pages/AssessmentsPortal'));
const EnhancedCourseDetail = React.lazy(() => import('./pages/EnhancedCourseDetail'));

// Student Track pages
const PlatformDashboard = React.lazy(() => import('./pages/studentTrack/PlatformDashboard'));
const CourseManagement = React.lazy(() => import('./pages/studentTrack/CourseManagement'));
const CourseManagementLegacy = React.lazy(() => import('./pages/studentTrack/CourseManagementLegacy'));
const InstructorManagement = React.lazy(() => import('./pages/studentTrack/InstructorManagement'));
const StudentManagement = React.lazy(() => import('./pages/studentTrack/StudentManagement'));
const ScheduleManagement = React.lazy(() => import('./pages/studentTrack/ScheduleManagement'));
const LearningContentControl = React.lazy(() => import('./pages/studentTrack/LearningContentControl'));
const ReportsAnalytics = React.lazy(() => import('./pages/studentTrack/ReportsAnalytics'));
const NotificationManagement = React.lazy(() => import('./pages/studentTrack/NotificationManagement'));
// const CoursesOverview = React.lazy(() => import('./pages/studentTrack/CoursesOverview'));
const CourseContentPage = React.lazy(() => import('./pages/studentTrack/CourseContentPage'));
const CurriculumPlan = React.lazy(() => import('./pages/studentTrack/CurriculumPlan'));

// Mentor pages
const InstructorDashboard = React.lazy(() => import('./pages/instructor/InstructorDashboard'));
const InstructorCourseManagement = React.lazy(() => import('./pages/instructor/CourseManagement'));
const InstructorProgramManagement = React.lazy(() => import('./pages/instructor/ProgramManagement'));
const InstructorStudentManagement = React.lazy(() => import('./pages/instructor/StudentManagement'));
const InstructorBatchManagement = React.lazy(() => import('./pages/instructor/BatchManagement'));
const InstructorReportsAnalytics = React.lazy(() => import('./pages/instructor/ReportsAnalytics'));
const InstructorLeaderboard = React.lazy(() => import('./pages/instructor/InstructorLeaderboard'));
const InstructorAttendance = React.lazy(() => import('./pages/instructor/InstructorAttendance'));
const AttendanceHistory = React.lazy(() => import('./pages/instructor/AttendanceHistory'));
const AttendanceDetail = React.lazy(() => import('./pages/instructor/AttendanceDetail'));

// Admin Attendance Routes

// Assignment Evaluation pages
const AssignmentDashboard = React.lazy(() => import('./pages/instructor/assignments/AssignmentDashboard'));
const SubmissionList = React.lazy(() => import('./pages/instructor/assignments/SubmissionList'));
const EvaluationWorkspace = React.lazy(() => import('./pages/instructor/assignments/EvaluationWorkspace'));
const EvaluationSuccess = React.lazy(() => import('./pages/instructor/assignments/EvaluationSuccess'));
const AssignmentForm = React.lazy(() => import('./pages/instructor/assignments/AssignmentForm'));
const AssignmentManagementPortal = React.lazy(() => import('./pages/instructor/assignments/AssignmentManagementPortal'));
const AssignmentAddPage = React.lazy(() => import('./pages/instructor/assignments/AssignmentAddPage'));
const AssignmentEditPage = React.lazy(() => import('./pages/instructor/assignments/AssignmentEditPage'));
const AssignmentViewPage = React.lazy(() => import('./pages/instructor/assignments/AssignmentViewPage'));
const DistributionPage = React.lazy(() => import('./pages/instructor/assignments/DistributionPage'));
const QuestionManagerPage = React.lazy(() => import('./pages/instructor/assignments/QuestionManagerPage'));
const QuestionImportPage = React.lazy(() => import('./pages/instructor/assignments/QuestionImportPage'));
const AssignmentQuestionsPage = React.lazy(() => import('./pages/instructor/assignments/AssignmentQuestionsPage'));
const QuestionAddPage = React.lazy(() => import('./pages/instructor/assignments/QuestionAddPage'));
const QuestionEditPage = React.lazy(() => import('./pages/instructor/assignments/QuestionEditPage'));
const InstructorRecordingManagement = React.lazy(() => import('./pages/instructor/InstructorRecordingManagement'));
const ExamReportPage = React.lazy(() => import('./pages/instructor/ExamReport'));
const AssignmentBatchProgressList = React.lazy(() => import('./pages/instructor/AssignmentBatchProgressList'));
const StudentResultsList = React.lazy(() => import('./pages/student/assignments/StudentResultsList'));
const StudentExamResults = React.lazy(() => import('./pages/student/assignments/StudentExamResults'));
const StudentLessonResults = React.lazy(() => import('./pages/student/assignments/StudentLessonResults'));


// Admin Evaluation Monitoring
const EvaluationMonitoring = React.lazy(() => import('./pages/admin/EvaluationMonitoring'));

// Admin Progress System
const BatchProgressPage = React.lazy(() => import('./pages/admin/BatchProgressPage'));
const CourseProgressPage = React.lazy(() => import('./pages/admin/CourseProgressPage'));
const CourseList = React.lazy(() => import('./pages/admin/CourseList'));
const CourseCompletionCertificate = React.lazy(() => import('./pages/admin/CourseCompletionCertificate'));
const CertificateView = React.lazy(() => import('./pages/admin/CertificateView'));

// Student Courses
// const StudentCourseList = React.lazy(() => import('./pages/student/CourseList'));

// Student Evaluation Result
const StudentResultPage = React.lazy(() => import('./pages/student/StudentResultPage'));

// Program Management pages
const ProgramManagement = React.lazy(() => import('./pages/studentTrack/ProgramManagement'));
const CreateProgram = React.lazy(() => import('./pages/ProgramManagement/CreateProgram'));
const EditProgram = React.lazy(() => import('./pages/ProgramManagement/EditProgram'));
// const ViewProgram = React.lazy(() => import('./pages/ProgramManagement/ViewProgram'));
// const CreateSubBatch = React.lazy(() => import('./pages/ProgramManagement/CreateSubBatch'));
const AssignStudents = React.lazy(() => import('./pages/ProgramManagement/AssignStudents'));

// Assessment Management System - New Pages
const AdminAssessmentList = React.lazy(() => import('./pages/assessments/AdminAssessmentList'));
const AdminAssessmentForm = React.lazy(() => import('./pages/assessments/AdminAssessmentForm'));
const AdminSubmissionView = React.lazy(() => import('./pages/assessments/AdminSubmissionView'));

const InstructorAssignedList = React.lazy(() => import('./pages/assessments/InstructorAssignedList'));
const InstructorSubmissionList = React.lazy(() => import('./pages/assessments/InstructorSubmissionList'));
const InstructorEvaluation = React.lazy(() => import('./pages/assessments/InstructorEvaluation'));

const StudentAssessmentList = React.lazy(() => import('./pages/assessments/StudentAssessmentList'));
const StudentAssessmentDetail = React.lazy(() => import('./pages/assessments/StudentAssessmentDetail'));
const StudentSubmit = React.lazy(() => import('./pages/assessments/StudentSubmit'));
const StudentResult = React.lazy(() => import('./pages/assessments/StudentResult'));
const StudentCurriculum = React.lazy(() => import('./pages/student/StudentCurriculum'));
const InstructorCourseView = React.lazy(() => import('./pages/InstructorCourseView'));

// Runway Management
const RunwayIndex = React.lazy(() => import('./pages/RunwayIndex'));


// Global Security Wrapper Component
const GlobalSecurityWrapper = ({ children }) => {
  // Apply global security restrictions
  useGlobalSecurity();
  
  return children;
};

const LoadingSpinner = () => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-background relative overflow-hidden">
    {/* Ambient background glow */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/10 rounded-full blur-[80px] animate-pulse"></div>

    <div className="relative flex flex-col items-center gap-6">
      <div className="relative group">
        <div className="absolute -inset-4 bg-primary/5 rounded-full blur-xl group-hover:bg-primary/10 transition-colors duration-500"></div>
        <img
          src={logo}
          alt="Navipro Nxt Loading"
          className="h-20 w-auto relative z-10 animate-[pulse_2s_ease-in-out_infinite] brightness-110 drop-shadow-[0_0_15px_rgba(79,70,229,0.3)]"
        />
      </div>

      <div className="flex flex-col items-center gap-2">
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-primary animate-[bounce_1s_infinite_0ms]"></span>
          <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-[bounce_1s_infinite_200ms]"></span>
          <span className="w-1.5 h-1.5 rounded-full bg-primary/30 animate-[bounce_1s_infinite_400ms]"></span>
        </div>
        <span className="text-xs font-bold text-muted-foreground uppercase tracking-[0.3em] opacity-60">Initializing</span>
      </div>
    </div>
  </div>
);

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  return user ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (user) {
    if (user.role === 'superadmin' || user.role === 'admin') return <Navigate to="/admin/dashboard" />;
    if (user.role === 'support') return <Navigate to="/admin/exam-users" />;
    if (user.role === 'instructor') return <Navigate to="/instructor/dashboard" />;
    return <Navigate to="/dashboard" />;
  }
  return children;
};

const LandingRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (user) {
    if (user.role === 'superadmin' || user.role === 'admin') return <Navigate to="/admin/dashboard" />;
    if (user.role === 'support') return <Navigate to="/admin/exam-users" />;
    if (user.role === 'instructor') return <Navigate to="/instructor/dashboard" />;
    return <Navigate to="/dashboard" />;
  }
  return children;
};

const Home = () => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/" />;
  if (user.role === 'superadmin' || user.role === 'admin') return <Navigate to="/admin/dashboard" />;
  if (user.role === 'support') return <Navigate to="/admin/exam-users" />;
  if (user.role === 'instructor') return <Navigate to="/instructor/dashboard" />;
  return <Navigate to="/dashboard" />;
};

const PlatformSuperadminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { isPlatformSuperadmin } = useAccess();

  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/" />;
  return isPlatformSuperadmin ? children : <Navigate to="/dashboard" replace />;
};

// Allows both platform superadmin AND institute admin
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { isPlatformSuperadmin } = useAccess();

  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/" />;
  const isInstituteAdmin = user.role === 'admin' && user.institute_id != null;
  return (isPlatformSuperadmin || isInstituteAdmin) ? children : <Navigate to="/dashboard" replace />;
};

const ModuleRoute = ({ children, moduleKey }) => {
  const { user, loading } = useAuth();
  const { loading: accessLoading, hasModule, isInstituteScopedUser } = useAccess();

  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/" />;
  if (isInstituteScopedUser && accessLoading) return <LoadingSpinner />;
  if (isInstituteScopedUser && moduleKey && !hasModule(moduleKey)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

const ProtectedDashboard = () => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (user?.role === 'instructor') return <Navigate to="/instructor/dashboard" />;
  if (user?.role === 'admin') return <Navigate to="/admin/dashboard" />;
  if (user?.role === 'support') return <Navigate to="/admin/exam-users" />;
  if (user?.type === 'exam_user') return <Navigate to={`/verify-and-pay?email=${encodeURIComponent(user?.email || '')}`} />;
  if (user?.type === 'paid_exam_user') return <Navigate to="/paid-exam-instructions" />;
  return <Dashboard />;
};

const PermissionRoute = ({ children, requiredPermission, redirectTo = '/dashboard', pageName = 'this page' }) => {
  const { user, loading } = useAuth();
  const { hasPermission } = usePermissions();
  const [showAlert, setShowAlert] = useState(true);
  
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/" />;
  
  // Check if user is superadmin institute (bypass permissions)
  const isSuperadminInstitute = user?.institute_id === parseInt(process.env.REACT_APP_SUPERADMIN_INSTITUTE_ID || '1');
  
  // Superadmin institute users bypass permission checks
  if (isSuperadminInstitute) {
    return children;
  }
  
  // Admin users are now subject to permission checks
  if (user.role === 'admin' && requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <PermissionAlert
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        permissionName={requiredPermission}
        pageName={pageName}
        redirectTo={redirectTo}
      />
    );
  }
  
  // Check specific permission for instructor role
  if (user.role === 'instructor' && requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <PermissionAlert
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        permissionName={requiredPermission}
        pageName={pageName}
        redirectTo={redirectTo}
      />
    );
  }
  
  return children;
};

function AppRoutes() {
  const { user, subscriptionData, refreshSubscriptionData } = useAuth();
  const [showBanner, setShowBanner] = useState(true);
  const location = useLocation();

  // Refresh subscription data when navigating to subscription-related routes
  useEffect(() => {
    const subscriptionRoutes = [
      '/subscription-lifecycle',
      '/superadmin/subscription-plans',
      '/superadmin/subscription-plans/new',
      '/superadmin/subscription-plans/',
      '/subscription-lifecycle/renew'
    ];
    
    const isSubscriptionRoute = subscriptionRoutes.some(route => 
      location.pathname.startsWith(route)
    );
    
    if (isSubscriptionRoute && user && subscriptionData) {
      refreshSubscriptionData();
    }
  }, [location.pathname, user, subscriptionData, refreshSubscriptionData]);
  
  // Check if access should be blocked
  const shouldBlockAccess = () => {
    if (!user) return false;
    if (!subscriptionData) return false;
    
    // Skip blocking for DEFAULT_INSTITUTE_ID users
    if (user.institute_id === parseInt(process.env.REACT_APP_DEFAULT_INSTITUTE_ID || '1')) {
      return false;
    }
    
    // Allow admins to access subscription-lifecycle routes even with expired subscription
    const currentPath = window.location.pathname;
    const isSubscriptionLifecycleRoute = currentPath.startsWith('/subscription-lifecycle');
    
    if (user.role === 'admin' && isSubscriptionLifecycleRoute) {
      return false;
    }

    if (subscriptionData.allow_access === false) {
      return true;
    }

    return ['suspended', 'deleted'].includes(subscriptionData.subscription_status);
  };
  
  if (shouldBlockAccess()) {
    return <AccessBlocked />;
  }
  
  return (
    <GlobalSecurityWrapper>
      <Suspense fallback={<LoadingSpinner />}>
        {/* Show subscription banner on all pages except for DEFAULT_INSTITUTE_ID users */}
        {showBanner && 
         subscriptionData?.subscription_message && 
         user?.institute_id !== parseInt(process.env.REACT_APP_DEFAULT_INSTITUTE_ID || '1') && (
          <SubscriptionBanner 
            subscriptionData={subscriptionData}
            onDismiss={() => setShowBanner(false)}
          />
        )}
        <Routes>
        <Route path="/" element={<InstituteLandingPage />} />
        <Route path="/inst/:slug" element={<InstituteLandingPage />} />
        <Route path="/courses-portal" element={<InstituteLandingPage />} />
        <Route path="/enroll" element={<Enroll />} />
        <Route path="/continue-course" element={<ContinueCourse />} />
        <Route path="/learn/:courseSlug" element={<ContinueCourse />} />

        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/inst/:slug/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
        <Route path="/auth/google/callback" element={<GoogleCallback />} />
        <Route path="/linkedin/callback" element={<LinkedInCallback />} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
        <Route path="/verify-email" element={<PublicRoute><VerifyAndPay /></PublicRoute>} />
        <Route path="/verify-and-pay" element={<VerifyAndPay />} />
        <Route path="/set-password" element={<PublicRoute><SetPassword /></PublicRoute>} />
        <Route path="/public-resume/:shareToken" element={<PublicResume />} />
        <Route path="/profile/resume/public/:shareToken" element={<PublicResume />} />
        <Route path="/dashboard" element={<PrivateRoute><ProtectedDashboard /></PrivateRoute>} />
        <Route path="/courses" element={<PrivateRoute><Courses /></PrivateRoute>} />
        <Route path="/courses/:id" element={<PrivateRoute><CourseDetail /></PrivateRoute>} />
        <Route path="/course-builder" element={<PrivateRoute><CourseBuilder /></PrivateRoute>} />
        <Route path="/course/:courseId/landing" element={<PrivateRoute><CourseLandingPage /></PrivateRoute>} />
        <Route path="/catalog" element={<PrivateRoute><CourseCatalog /></PrivateRoute>} />
        <Route path="/leaderboard" element={<PrivateRoute><Leaderboard /></PrivateRoute>} />
        <Route path="/student/dashboard" element={<PrivateRoute><StudentDashboard /></PrivateRoute>} />

        {/* Test Module */}
        <Route path="/tests" element={<PrivateRoute><Tests /></PrivateRoute>} />
        <Route path="/tests/:subjectId/levels" element={<PrivateRoute><TestRoadmap /></PrivateRoute>} />
        <Route path="/tests/:subjectId/levels/:levelId/instructions" element={<PrivateRoute><TestInstructionPage /></PrivateRoute>} />
        <Route path="/tests/:subjectId/levels/:levelId/test" element={<PrivateRoute><TestEnginePage /></PrivateRoute>} />
        <Route path="/tests/:subjectId/levels/:levelId/results" element={<PrivateRoute><TestResultPage /></PrivateRoute>} />

        <Route path="/assignments" element={<PrivateRoute><Assignments /></PrivateRoute>} />
        <Route path="/assignments/:subjectId/levels" element={<PrivateRoute><LevelRoadmap /></PrivateRoute>} />
        <Route path="/assignments/:subjectId/programming" element={<PrivateRoute><ProgrammingEngine /></PrivateRoute>} />
        <Route path="/assignments/:subjectId/levels/:levelId/instructions" element={<PrivateRoute><TestInstructions /></PrivateRoute>} />
        <Route path="/assignments/:subjectId/levels/:levelId/test" element={<PrivateRoute><TestEngine /></PrivateRoute>} />
        <Route path="/assignments/:subjectId/levels/:levelId/results" element={<PrivateRoute><TestResultPage /></PrivateRoute>} />
        <Route path="/assignments/:subjectId/levels/:levelId/results/:attemptId" element={<PrivateRoute><TestResultPage /></PrivateRoute>} />
        <Route path="/student/assignments/:assignmentId/results" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><StudentResultsList /></ModuleRoute></PrivateRoute>} />
        <Route path="/student/assignments/:assignmentId/exam-results" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><StudentExamResults /></ModuleRoute></PrivateRoute>} />
        <Route path="/student/lessons/:lessonId/results" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><StudentLessonResults /></ModuleRoute></PrivateRoute>} />
        <Route path="/assignments/:assignmentId/batch-progress" element={<PrivateRoute><AssignmentBatchProgressList /></PrivateRoute>} />
        <Route path="/assignments/:assignmentId/exam-report" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><ExamReportPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/company-questions" element={<PrivateRoute><CompanyQuestions /></PrivateRoute>} />
        <Route path="/jobs" element={<PrivateRoute><Jobs /></PrivateRoute>} />
        <Route path="/bookmarks" element={<PrivateRoute><Bookmarks /></PrivateRoute>} />
        <Route path="/wallet" element={<PrivateRoute><Wallet /></PrivateRoute>} />
        <Route path="/profile" element={<PrivateRoute><DynamicProfile /></PrivateRoute>} />
        <Route path="/resume-builder" element={<PrivateRoute><ResumeFullPage /></PrivateRoute>} />
        <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
        <Route path="/admin/dashboard" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
        <Route path="/analytics" element={<PrivateRoute><Analytics /></PrivateRoute>} />
        <Route path="/live-classes" element={<PrivateRoute><LiveClasses /></PrivateRoute>} />
        <Route path="/live-class/:classId" element={<PrivateRoute><EnhancedLiveClass /></PrivateRoute>} />
        <Route path="/code-lab" element={<PrivateRoute><EnhancedCodeLab /></PrivateRoute>} />
        <Route path="/code-lab-basic" element={<PrivateRoute><CodeLab /></PrivateRoute>} />
        <Route path="/forum" element={<PrivateRoute><Forum /></PrivateRoute>} />
        <Route path="/chat" element={<PrivateRoute><Chat /></PrivateRoute>} />
        <Route path="/certificates" element={<PrivateRoute><Certificates /></PrivateRoute>} />
        <Route path="/certificate-view" element={<PrivateRoute><CertificateView /></PrivateRoute>} />
        <Route path="/learning-paths" element={<PrivateRoute><LearningPaths /></PrivateRoute>} />
        <Route path="/recordings" element={<PrivateRoute><Recordings /></PrivateRoute>} />
        <Route path="/recordings/:id" element={<PrivateRoute><RecordingDetail /></PrivateRoute>} />

        {/* Payment Module Routes */}
        <Route path="/admin/payments" element={<PrivateRoute><AdminPayments /></PrivateRoute>} />
        <Route path="/admin/institute-subscription-payments" element={<PrivateRoute><AdminRoute><InstituteSubscriptionPayments /></AdminRoute></PrivateRoute>} />
        <Route path="/admin/institute-subscription-plans" element={<PrivateRoute><PlatformSuperadminRoute><InstituteSubscriptionPlans /></PlatformSuperadminRoute></PrivateRoute>} />
        <Route path="/admin/student-payment-support" element={<PrivateRoute><AdminStudentPaymentSupport /></PrivateRoute>} />
        <Route path="/admin/referral-settings" element={<PrivateRoute><PlatformSuperadminRoute><ReferralSettingsPage /></PlatformSuperadminRoute></PrivateRoute>} />
        <Route path="/student/payments" element={<PrivateRoute><StudentPayments /></PrivateRoute>} />
        <Route path="/admin/course-list" element={<PrivateRoute><CourseList /></PrivateRoute>} />
        <Route path="/admin/course-completion-certificate" element={<PrivateRoute><CourseCompletionCertificate /></PrivateRoute>} />
        <Route path="/admin/exam-users" element={<PrivateRoute><ExamUsers /></PrivateRoute>} />
        <Route path="/admin/paid-exam-marks" element={<PrivateRoute><PaidExamMarks /></PrivateRoute>} />
        <Route path="/paid-exam-instructions" element={<PrivateRoute><PaidExamInstructions /></PrivateRoute>} />
        <Route path="/admin/support-users" element={<PrivateRoute><SupportUserManagement /></PrivateRoute>} />
        {/* <Route path="/checkout" element={<PrivateRoute><Checkout /></PrivateRoute>} /> */}

        {/* New Enhanced Routes */}
        <Route path="/admin-panel" element={<PrivateRoute><PlatformSuperadminRoute><AdminPanel /></PlatformSuperadminRoute></PrivateRoute>} />
        <Route path="/jobs-portal" element={<PrivateRoute><JobsPortal /></PrivateRoute>} />
        <Route path="/community-forum" element={<PrivateRoute><CommunityForum /></PrivateRoute>} />
        <Route path="/notifications-center" element={<PrivateRoute><NotificationsCenter /></PrivateRoute>} />
        {/* <Route path="/live-classes-portal" element={<PrivateRoute><LiveClassesPortal /></PrivateRoute>} /> */}
        <Route path="/assessments-portal" element={<PrivateRoute><AssessmentsPortal /></PrivateRoute>} />
        <Route path="/course-detail/:id" element={<PrivateRoute><EnhancedCourseDetail /></PrivateRoute>} />

        {/* Runway Management */}
        <Route path="/admin/runways" element={<PrivateRoute><ModuleRoute moduleKey="runway_management"><RunwayIndex mode="admin" /></ModuleRoute></PrivateRoute>} />

        {/* Student Track */}
        <Route path="/student-track/platform-dashboard" element={<PrivateRoute><PlatformDashboard /></PrivateRoute>} />
        <Route path="/student-track/course-management" element={<PrivateRoute><PermissionRoute requiredPermission="course_access" pageName="Course Management"><ModuleRoute moduleKey="course_management"><CourseManagement /></ModuleRoute></PermissionRoute></PrivateRoute>} />
        <Route path="/student-track/course-management-legacy" element={<PrivateRoute><PermissionRoute requiredPermission="course_access" pageName="Course Management (Legacy)"><ModuleRoute moduleKey="course_management"><CourseManagementLegacy /></ModuleRoute></PermissionRoute></PrivateRoute>} />
        <Route path="/student-track/instructor-management" element={<PrivateRoute><PermissionRoute requiredPermission="user_access" pageName="Mentor Management"><ModuleRoute moduleKey="instructor_management"><InstructorManagement /></ModuleRoute></PermissionRoute></PrivateRoute>} />
        <Route path="/student-track/student-management" element={<PrivateRoute><PermissionRoute requiredPermission="user_access" pageName="Student Management"><ModuleRoute moduleKey="student_management"><StudentManagement /></ModuleRoute></PermissionRoute></PrivateRoute>} />
        <Route path="/student-track/schedule-management" element={<PrivateRoute><PermissionRoute requiredPermission="batch_access" pageName="Schedule Management"><ModuleRoute moduleKey="schedule_management"><ScheduleManagement /></ModuleRoute></PermissionRoute></PrivateRoute>} />
        <Route path="/student-track/learning-content-control" element={<PrivateRoute><LearningContentControl /></PrivateRoute>} />
        <Route path="/student-track/reports-analytics" element={<PrivateRoute><ReportsAnalytics /></PrivateRoute>} />
        <Route path="/student-track/notification-management" element={<PrivateRoute><NotificationManagement /></PrivateRoute>} />
        <Route path="/student-track/program-management" element={<PrivateRoute><PermissionRoute requiredPermission="batch_access" pageName="Program Management"><ModuleRoute moduleKey="program_management"><ProgramManagement /></ModuleRoute></PermissionRoute></PrivateRoute>} />
        {/* <Route path="/student-track/program-management/:id" element={<PrivateRoute><ViewProgram /></PrivateRoute>} /> */}
        {/* <Route path="/courses-overview" element={<PrivateRoute><CoursesOverview /></PrivateRoute>} /> */}
        <Route path="/curriculum-plan/:entryYear" element={<PrivateRoute><ModuleRoute moduleKey="course_management"><CurriculumPlan /></ModuleRoute></PrivateRoute>} />
        <Route path="/curriculum-plan/:entryYear/course-content" element={<PrivateRoute><ModuleRoute moduleKey="course_management"><CourseContentPage /></ModuleRoute></PrivateRoute>} />

        {/* Program Management */}
        <Route path="/admin/programs" element={<PrivateRoute><PermissionRoute requiredPermission="batch_access"><ModuleRoute moduleKey="program_management"><ProgramManagement /></ModuleRoute></PermissionRoute></PrivateRoute>} />
        <Route path="/admin/programs/create" element={<PrivateRoute><PermissionRoute requiredPermission="batch_access"><ModuleRoute moduleKey="program_management"><CreateProgram /></ModuleRoute></PermissionRoute></PrivateRoute>} />
        {/* <Route path="/admin/programs/:id" element={<PrivateRoute><ViewProgram /></PrivateRoute>} /> */}
        <Route path="/admin/programs/:id/edit" element={<PrivateRoute><PermissionRoute requiredPermission="batch_access"><ModuleRoute moduleKey="program_management"><EditProgram /></ModuleRoute></PermissionRoute></PrivateRoute>} />
        {/* <Route path="/admin/programs/:programId/years/:yearId/sub-batch/create" element={<PrivateRoute><CreateSubBatch /></PrivateRoute>} /> */}
        <Route path="/admin/programs/:programId/years/:yearId/sub-batches/:subBatchId/assign-students" element={<PrivateRoute><PermissionRoute requiredPermission="batch_access"><ModuleRoute moduleKey="program_management"><AssignStudents /></ModuleRoute></PermissionRoute></PrivateRoute>} />

        {/* Admin Progress System */}
        <Route path="/admin/batch-progress/:batchId" element={<PrivateRoute><BatchProgressPage /></PrivateRoute>} />
        <Route path="/admin/course-progress/:batchId/:courseId" element={<PrivateRoute><CourseProgressPage /></PrivateRoute>} />

        {/* Mentor Progress System */}
        <Route path="/instructor/batch-progress/:batchId" element={<PrivateRoute><BatchProgressPage /></PrivateRoute>} />
        <Route path="/instructor/course-progress/:batchId/:courseId" element={<PrivateRoute><CourseProgressPage /></PrivateRoute>} />

        {/* Mentor Module */}
        <Route path="/instructor" element={<PrivateRoute><InstructorDashboard /></PrivateRoute>} />
        <Route path="/instructor/dashboard" element={<PrivateRoute><InstructorDashboard /></PrivateRoute>} />
        <Route path="/instructor/courses" element={<PrivateRoute><PermissionRoute requiredPermission="course_access" pageName="Course Management"><InstructorCourseManagement /></PermissionRoute></PrivateRoute>} />
        <Route path="/instructor/programs" element={<PrivateRoute><PermissionRoute requiredPermission="batch_access" pageName="Program Management"><InstructorProgramManagement /></PermissionRoute></PrivateRoute>} />
        <Route path="/instructor/students" element={<PrivateRoute><PermissionRoute requiredPermission="user_access" pageName="Student Management"><InstructorStudentManagement /></PermissionRoute></PrivateRoute>} />
        <Route path="/instructor/batches" element={<PrivateRoute><PermissionRoute requiredPermission="batch_access" pageName="Batch Management"><InstructorBatchManagement /></PermissionRoute></PrivateRoute>} />
        <Route path="/instructor/schedule" element={<PrivateRoute><PermissionRoute requiredPermission="batch_access" pageName="Batch Schedule"><InstructorBatchManagement /></PermissionRoute></PrivateRoute>} />
        <Route path="/instructor/reports" element={<PrivateRoute><InstructorReportsAnalytics /></PrivateRoute>} />
        <Route path="/instructor/leaderboard" element={<PrivateRoute><InstructorLeaderboard /></PrivateRoute>} />
        <Route path="/instructor/attendance" element={<PrivateRoute><ModuleRoute moduleKey="attendance"><InstructorAttendance /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/attendance-history" element={<PrivateRoute><ModuleRoute moduleKey="attendance"><AttendanceHistory /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/attendance-detail/:date/:batch" element={<PrivateRoute><ModuleRoute moduleKey="attendance"><AttendanceDetail /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/recordings" element={<PrivateRoute><InstructorRecordingManagement /></PrivateRoute>} />
        <Route path="/instructor/runways" element={<PrivateRoute><ModuleRoute moduleKey="runway_management"><RunwayIndex mode="instructor" /></ModuleRoute></PrivateRoute>} />

        {/* Assignment Evaluation */}
        <Route path="/instructor/assignments" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><AssignmentDashboard /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/assignments/create" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><AssignmentForm /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/assignments/:assignmentId/submissions" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><SubmissionList /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/assignments/:assignmentId/evaluate/:submissionId" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><EvaluationWorkspace /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/assignments/:assignmentId/evaluate/success" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><EvaluationSuccess /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/assignments/portal" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><AssignmentManagementPortal /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/assignments/new" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><AssignmentAddPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/exam-report" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><ExamReportPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/assignments/:id/edit" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><AssignmentEditPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/assignments/:id/view" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><AssignmentViewPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/assignments/:id/distribution" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><DistributionPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/assignments/questions" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><QuestionManagerPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/assignments/questions/import" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><QuestionImportPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/assignments/:id/questions" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><AssignmentQuestionsPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/question-manager" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><QuestionManagerPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/questions" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><QuestionManagerPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/questions/new" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><QuestionAddPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/assignments/:id/questions/new" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><QuestionAddPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/questions/:qId/edit" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><QuestionEditPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/assignments/:id/questions/:qId/edit" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><QuestionEditPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/assignments/:assignmentId/batch-progress" element={<PrivateRoute><AssignmentBatchProgressList /></PrivateRoute>} />
        <Route path="/instructor/assignments/:assignmentId/exam-report" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><ExamReportPage /></ModuleRoute></PrivateRoute>} />
        
        {/* Admin Assignment Management */}
        <Route path="/admin/assignments/portal" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><AssignmentManagementPortal /></ModuleRoute></PrivateRoute>} />
        <Route path="/admin/exam-report" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><ExamReportPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/admin/assignments/new" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><AssignmentAddPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/admin/assignments/:id/edit" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><AssignmentEditPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/admin/assignments/:id/view" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><AssignmentViewPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/admin/assignments/:id/distribution" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><DistributionPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/admin/assignments/questions" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><QuestionManagerPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/admin/assignments/questions/import" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><QuestionImportPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/admin/assignments/:id/questions" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><AssignmentQuestionsPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/admin/assignments/:id/questions/new" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><QuestionAddPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/admin/questions" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><QuestionManagerPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/admin/questions/new" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><QuestionAddPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/admin/questions/:qId/edit" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><QuestionEditPage /></ModuleRoute></PrivateRoute>} />
        <Route path="/admin/assignments/:id/questions/:qId/edit" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><QuestionEditPage /></ModuleRoute></PrivateRoute>} />


        <Route path="/admin/attendance" element={<PrivateRoute><ModuleRoute moduleKey="attendance"><InstructorAttendance /></ModuleRoute></PrivateRoute>} />
        <Route path="/admin/attendance-history" element={<PrivateRoute><ModuleRoute moduleKey="attendance"><AttendanceHistory /></ModuleRoute></PrivateRoute>} />
        <Route path="/admin/attendance/:date/:batch" element={<PrivateRoute><ModuleRoute moduleKey="attendance"><AttendanceDetail /></ModuleRoute></PrivateRoute>} />
        {/* Question Manager */}
        <Route path="/admin/question-manager" element={<PrivateRoute><ModuleRoute moduleKey="assignments"><QuestionManagerPage /></ModuleRoute></PrivateRoute>} />




        <Route path="/admin/evaluation-monitoring" element={<PrivateRoute><EvaluationMonitoring /></PrivateRoute>} />
        <Route path="/admin/recording-management" element={<PrivateRoute><AdminRecordingManagement /></PrivateRoute>} />

        {/* Student Results */}
        <Route path="/student/assignments/:assignmentId/result" element={<PrivateRoute><StudentResultPage /></PrivateRoute>} />

        {/* Assessment Management System - Admin */}
        <Route path="/admin/assessments" element={<PrivateRoute><ModuleRoute moduleKey="assessments"><AdminAssessmentList /></ModuleRoute></PrivateRoute>} />
        <Route path="/admin/assessments/create" element={<PrivateRoute><ModuleRoute moduleKey="assessments"><AdminAssessmentForm /></ModuleRoute></PrivateRoute>} />
        <Route path="/admin/assessments/:id/edit" element={<PrivateRoute><ModuleRoute moduleKey="assessments"><AdminAssessmentForm /></ModuleRoute></PrivateRoute>} />
        <Route path="/admin/assessments/:id/submissions" element={<PrivateRoute><ModuleRoute moduleKey="assessments"><AdminSubmissionView /></ModuleRoute></PrivateRoute>} />
        <Route path="/admin/assessments/:assignmentId/evaluate/:submissionId" element={<PrivateRoute><ModuleRoute moduleKey="assessments"><InstructorEvaluation /></ModuleRoute></PrivateRoute>} />

        {/* Assessment Management System - Mentor */}
        <Route path="/instructor/assessments" element={<PrivateRoute><ModuleRoute moduleKey="assessments"><InstructorAssignedList /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/assessments/create" element={<PrivateRoute><ModuleRoute moduleKey="assessments"><AdminAssessmentForm /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/assessments/:id/edit" element={<PrivateRoute><ModuleRoute moduleKey="assessments"><AdminAssessmentForm /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/assessments/:id/submissions" element={<PrivateRoute><ModuleRoute moduleKey="assessments"><InstructorSubmissionList /></ModuleRoute></PrivateRoute>} />
        <Route path="/instructor/assessments/:assignmentId/evaluate/:submissionId" element={<PrivateRoute><ModuleRoute moduleKey="assessments"><InstructorEvaluation /></ModuleRoute></PrivateRoute>} />

        {/* Assessment Management System - Student */}
        <Route path="/student/assessments" element={<PrivateRoute><StudentAssessmentList /></PrivateRoute>} />
        <Route path="/student/assessments/:id" element={<PrivateRoute><StudentAssessmentDetail /></PrivateRoute>} />
        <Route path="/student/assessments/:id/submit" element={<PrivateRoute><StudentSubmit /></PrivateRoute>} />
        <Route path="/student/assessments/:id/result" element={<PrivateRoute><StudentResult /></PrivateRoute>} />
        <Route path="/student/curriculum" element={<PrivateRoute><StudentCurriculum /></PrivateRoute>} />
        <Route path="/instructor/course-view/:courseId" element={<PrivateRoute><InstructorCourseView /></PrivateRoute>} />

        {/* Multi-Tenant / Institute Management */}
        <Route path="/register-institute" element={<InstituteRegister />} />
        <Route path="/institute/onboarding" element={<PrivateRoute><InstituteOnboarding /></PrivateRoute>} />
        <Route path="/institute/branding" element={<PrivateRoute><ModuleRoute moduleKey="branding"><InstituteBranding /></ModuleRoute></PrivateRoute>} />
        <Route path="/institute/analytics" element={<PrivateRoute><ModuleRoute moduleKey="analytics"><InstituteAnalytics /></ModuleRoute></PrivateRoute>} />
        <Route path="/superadmin/institutes" element={<PrivateRoute><PlatformSuperadminRoute><SuperAdminInstitutes /></PlatformSuperadminRoute></PrivateRoute>} />
        <Route path="/superadmin/institutes/new" element={<PrivateRoute><PlatformSuperadminRoute><SuperAdminInstituteCreate /></PlatformSuperadminRoute></PrivateRoute>} />
        <Route path="/superadmin/institutes/:instituteId" element={<PrivateRoute><PlatformSuperadminRoute><SuperAdminInstituteDetails /></PlatformSuperadminRoute></PrivateRoute>} />
        <Route path="/superadmin/institutes/:instituteId/delete" element={<PrivateRoute><PlatformSuperadminRoute><SuperAdminInstituteDelete /></PlatformSuperadminRoute></PrivateRoute>} />

        {/* Subscription Plans Management */}
        <Route path="/superadmin/subscription-plans" element={<PrivateRoute><PlatformSuperadminRoute><SubscriptionPlans /></PlatformSuperadminRoute></PrivateRoute>} />
        <Route path="/superadmin/manage-permissions" element={<PrivateRoute><PlatformSuperadminRoute><ManagePermissions /></PlatformSuperadminRoute></PrivateRoute>} />
        <Route path="/superadmin/email-templates" element={<PrivateRoute><PlatformSuperadminRoute><EmailTemplates /></PlatformSuperadminRoute></PrivateRoute>} />
        <Route path="/superadmin/subscription-plans/new" element={<PrivateRoute><PlatformSuperadminRoute><SubscriptionPlanManager /></PlatformSuperadminRoute></PrivateRoute>} />
        <Route path="/superadmin/subscription-plans/:planId/edit" element={<PrivateRoute><PlatformSuperadminRoute><SubscriptionPlanManager /></PlatformSuperadminRoute></PrivateRoute>} />

        {/* Subscription Lifecycle Routes */}
        <Route path="/subscription-lifecycle/renew" element={<PrivateRoute><SubscriptionRenewal /></PrivateRoute>} />

          </Routes>
      </Suspense>
    </GlobalSecurityWrapper>
  );
}

function App() {
  const { i18n } = useTranslation();

  React.useEffect(() => {
    document.documentElement.setAttribute('dir', i18n.language === 'ar' ? 'rtl' : 'ltr');
  }, [i18n.language]);

  return (
    <ThemeProvider>
      <AuthProvider>
        <InstituteProvider>
          <AccessProvider>
            <PermissionProvider>
              <BrowserRouter>
                <AppRoutes />
                <Toaster />
              </BrowserRouter>
            </PermissionProvider>
          </AccessProvider>
        </InstituteProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
